import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { useRef, useState, useEffect } from "react";

// Logo
const LOGO_IMAGE = "https://customer-assets.emergentagent.com/job_craft-homes-7/artifacts/m2vqyuwk_33233402-ca77-44e6-9ac5-76afe5ec4b84.png";

// Video background - luxury bathroom
const HERO_VIDEO = "https://videos.pexels.com/video-files/6585634/6585634-uhd_2560_1440_30fps.mp4";

// Hero fallback - Modern old money style English mansion
const HERO_FALLBACK = "https://customer-assets.emergentagent.com/job_3e849b0a-3eb9-4a57-9473-38116027ffed/artifacts/ndwps0xt_pawiale_Ultra-realistic_luxury_bathroom_renovation_walk-in_sh_846b3633-73d0-4f37-af98-b865339690f6_2%20%281%29.png";

// Modern wealthy English mansion for hero - old money style
const MODERN_MANSION = "https://customer-assets.emergentagent.com/job_3e849b0a-3eb9-4a57-9473-38116027ffed/artifacts/ndwps0xt_pawiale_Ultra-realistic_luxury_bathroom_renovation_walk-in_sh_846b3633-73d0-4f37-af98-b865339690f6_2%20%281%29.png";

// Modern bathroom images
const MODERN_BATHROOM = "https://images.unsplash.com/photo-1756079664354-34944e001f6d?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85";
const MODERN_BATHROOM_GOLD = "https://images.unsplash.com/photo-1764475501545-d5cc9719af1a?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85";

// Modern interior/refurbishment images  
const MODERN_INTERIOR = "https://images.unsplash.com/photo-1749704647804-81338ade5546?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85";
const MODERN_LIVING = "https://images.unsplash.com/photo-1759238136818-7b00ec9e782a?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85";

// Subtle bathroom accent
const SUBTLE_BATHROOM = "https://images.unsplash.com/photo-1564540583246-934409427776?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85";

// Accent images - modern marble
const MARBLE_TEXTURE = "https://images.unsplash.com/photo-1769763917830-7b9c8317329d?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85";
const MARBLE_DETAIL = "https://images.unsplash.com/photo-1763485957127-5645e9348426?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85";

// Craftsmanship detail images
const CRAFT_HERO_NICHE = "https://static.prod-images.emergentagent.com/jobs/f4afc9c8-562b-4ae6-a3a1-517d9e3e9297/images/fe22a861e4f083eb8b191d7b89b14514df2d7d595e35f3100ffb009e417b517c.jpeg";
const CRAFT_MITRE_1 = "https://static.prod-images.emergentagent.com/jobs/f4afc9c8-562b-4ae6-a3a1-517d9e3e9297/images/2a35974972798f3b35ca9490c8543289ffc9d757f8cd70c88fa2fc2145cdade0.jpeg";
const CRAFT_MITRE_2 = "https://static.prod-images.emergentagent.com/jobs/f4afc9c8-562b-4ae6-a3a1-517d9e3e9297/images/7927e8e55f05dd50c0577a9432d8119bbb0a8361b0cd36f6207492d2d4573cb7.jpeg";
const CRAFT_NICHE_DETAIL = "https://static.prod-images.emergentagent.com/jobs/f4afc9c8-562b-4ae6-a3a1-517d9e3e9297/images/ceef986871019ad328989683cb2a7f43c6fb478c540c30cbe63a2e2f4d84d4b2.jpeg";
const CRAFT_SILICONE = "https://static.prod-images.emergentagent.com/jobs/f4afc9c8-562b-4ae6-a3a1-517d9e3e9297/images/98ceda17e040878a97cf771f24a569e5227961083972be5daefd390580c8e0b2.jpeg";
const CRAFT_SILICONE_GLASS = "https://static.prod-images.emergentagent.com/jobs/f4afc9c8-562b-4ae6-a3a1-517d9e3e9297/images/5fd857cefc47f1a73af1ad4324066c3fb2aa61359447e93d1751c9b73aab0851.jpeg";
const CRAFT_FLOOR_SHOWER = "https://static.prod-images.emergentagent.com/jobs/f4afc9c8-562b-4ae6-a3a1-517d9e3e9297/images/5a61f5e4983413f61fa6a4aedca7eb30a46a11deaa1f882101d7815c5aa6d299.jpeg";
const CRAFT_SHOWER_MIXER = "https://static.prod-images.emergentagent.com/jobs/f4afc9c8-562b-4ae6-a3a1-517d9e3e9297/images/15b9bfeb9e2559e91f12688183c8f782a893bd9a2ee8042a279108f137d85314.jpeg";
const CRAFT_VANITY = "https://static.prod-images.emergentagent.com/jobs/f4afc9c8-562b-4ae6-a3a1-517d9e3e9297/images/bf26e17dbe836c8ad80ff8f1d9b63ac633580a9067b27da330d960cda7fbe15d.jpeg";
const CRAFT_JUNCTION = "https://static.prod-images.emergentagent.com/jobs/f4afc9c8-562b-4ae6-a3a1-517d9e3e9297/images/47080bcffea24add3258998adf9fa710fbd3b36e088a0e8b4601f41bfbe1b523.jpeg";

// Placeholder
const PLACEHOLDER_IMAGE = "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1200&q=80";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 }
};

// Project slideshow data - featured projects from portfolio
const slideshowProjects = [
  {
    title: "Luxury Bathroom Design and Installation",
    coverImage: "/images/luxury-bathroom/after_1_full_view.webp",
    hasBeforeAfter: true
  },
  {
    title: "Complete Garden Transformation",
    coverImage: "/images/garden-transformation/after-2.jpg",
    hasBeforeAfter: true
  },
  {
    title: "Luxury Ensuite Transformation – Gold Finish",
    coverImage: "/images/luxury-ensuite-gold/after-1.jpg",
    hasBeforeAfter: true
  },
  {
    title: "Conservatory Conversion to Media Wall",
    coverImage: "/images/conservatory-media-wall/after-2.jpg",
    hasBeforeAfter: true
  },
  {
    title: "Ensuite Transformation",
    coverImage: "/images/ensuite-transformation/after-4.jpg",
    hasBeforeAfter: true
  }
];

const ProjectSlideshow = () => {
  const scrollRef = useRef(null);
  const [isInView, setIsInView] = useState(false);

  // Duplicate projects for seamless infinite scroll
  const duplicatedProjects = [...slideshowProjects, ...slideshowProjects, ...slideshowProjects, ...slideshowProjects, ...slideshowProjects, ...slideshowProjects];

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !isInView) return;
    
    let animationId;
    let scrollPos = 0;
    const speed = 0.8;
    
    const animate = () => {
      scrollPos += speed;
      if (scrollPos >= container.scrollWidth / 2) {
        scrollPos = 0;
      }
      container.scrollLeft = scrollPos;
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- all vars (animate, animationId, speed, scrollPos) are defined inside this effect; only isInView is external
  }, [isInView]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.1 }
    );
    const currentRef = scrollRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on mount; scrollRef/setIsInView are stable
  }, []);

  return (
    <div 
      ref={scrollRef}
      className="flex gap-6 overflow-hidden px-6 md:px-12"
      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
    >
      {duplicatedProjects.map((project, index) => (
        <Link
          to="/portfolio"
          key={`${project.title}-${index}`}
          className="flex-shrink-0 w-[320px] md:w-[400px] group cursor-pointer"
          data-testid={`slideshow-project-${index}`}
        >
          <div className="relative aspect-[4/3] overflow-hidden rounded-sm">
            <img
              src={project.coverImage}
              alt={project.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-transparent to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300" />
            {project.hasBeforeAfter && (
              <span className="absolute top-3 right-3 bg-bronze/90 text-charcoal text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-sm">
                Before & After
              </span>
            )}
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <h3 className="font-heading text-lg text-stone-50 leading-snug">{project.title}</h3>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
};

const Home = () => {
  // Parallax refs
  const heroRef = useRef(null);
  const section1Ref = useRef(null);
  const section3Ref = useRef(null);

  // Parallax scroll transforms
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const { scrollYProgress: section1Progress } = useScroll({
    target: section1Ref,
    offset: ["start end", "end start"]
  });

  const heroY = useTransform(heroProgress, [0, 1], ["0%", "30%"]);
  const section1Y = useTransform(section1Progress, [0, 1], ["5%", "-5%"]);

  return (
    <div data-testid="home-page" className="overflow-hidden">
      {/* Hero Section with Modern Mansion Background */}
      <section 
        ref={heroRef}
        className="relative min-h-screen flex items-center overflow-hidden" 
        data-testid="hero-section"
      >
        {/* Static Image Background - Modern Mansion */}
        <motion.div 
          className="absolute inset-0"
          style={{ y: heroY }}
        >
          <img
            src={MODERN_MANSION}
            alt="Prestigious modern London mansion"
            className="w-full h-full object-cover scale-110"
            data-testid="hero-image"
          />
          {/* Gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/90 via-charcoal/60 to-charcoal/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 via-transparent to-transparent" />
        </motion.div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full py-20">
          {/* Left side - Logo and Text */}
          <div className="max-w-2xl">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              transition={{ duration: 1, ease: "easeOut" }}
              className="mb-8"
            >
              <img
                src={LOGO_IMAGE}
                alt="PJMW Services"
                className="h-32 md:h-40 lg:h-48 w-auto drop-shadow-2xl"
                data-testid="hero-logo"
              />
            </motion.div>
            
            <motion.h1
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl text-stone-50 mb-6 leading-[0.95] tracking-tight"
            >
              Building with Vision.<br />
              Crafted with Precision.<br />
              <span className="text-bronze">Finished to the Highest Standard.</span>
            </motion.h1>
            
            <motion.p
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
              className="font-body text-lg md:text-xl text-stone-50/90 mb-12 max-w-xl leading-relaxed"
            >
              Bespoke Bathrooms & Property Transformations Designed Around You.
            </motion.p>
            
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              transition={{ duration: 1, delay: 0.6, ease: "easeOut" }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-view-work-btn"
                className="group relative inline-flex items-center justify-center gap-2 uppercase tracking-widest text-xs font-semibold px-10 py-5 bg-bronze text-charcoal rounded-sm overflow-hidden transition-all duration-500"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Get a Free Quote
                  <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
                <div className="absolute inset-0 bg-bronze-light transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 shadow-[0_0_30px_rgba(201,162,39,0.5)]" />
              </Link>
              <Link
                to="/portfolio"
                data-testid="hero-explore-services-btn"
                className="group relative inline-flex items-center justify-center gap-2 uppercase tracking-widest text-xs font-semibold px-10 py-5 bg-transparent border border-stone-50/40 text-stone-50 rounded-sm overflow-hidden transition-all duration-500 hover:border-bronze"
              >
                <span className="relative z-10 flex items-center gap-2">
                  View Our Work
                  <Play size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:scale-110" />
                </span>
                <div className="absolute inset-0 bg-bronze/20 transform scale-y-0 group-hover:scale-y-100 transition-transform duration-500 origin-bottom" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 shadow-[0_0_25px_rgba(201,162,39,0.4)]" />
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-bronze to-transparent" />
        </motion.div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-charcoal relative overflow-hidden" data-testid="why-choose-us-section">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(201,162,39,0.3) 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-body text-xs uppercase tracking-widest text-bronze mb-4 block">Why Choose Us</span>
            <h2 className="font-heading text-3xl md:text-4xl text-stone-50">Built on Experience. Driven by Quality.</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Stat 1 */}
            <motion.div
              className="text-center p-8 border border-stone-50/10 rounded-sm"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-heading text-5xl md:text-6xl text-bronze block mb-3">10+</span>
              <h3 className="font-heading text-lg text-stone-50 mb-2">Years of Experience</h3>
              <p className="font-body text-sm text-stone-50/60 leading-relaxed">Over a decade of delivering high-quality bathroom installations and property refurbishments across the region.</p>
            </motion.div>

            {/* Stat 2 */}
            <motion.div
              className="text-center p-8 border border-stone-50/10 rounded-sm"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <span className="font-heading text-5xl md:text-6xl text-bronze block mb-3">100+</span>
              <h3 className="font-heading text-lg text-stone-50 mb-2">Projects Completed</h3>
              <p className="font-body text-sm text-stone-50/60 leading-relaxed">From luxury bathrooms to full property transformations, every project is finished to the highest standard.</p>
            </motion.div>

            {/* Stat 3 */}
            <motion.div
              className="text-center p-8 border border-stone-50/10 rounded-sm"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <span className="font-heading text-5xl md:text-6xl text-bronze block mb-3">100%</span>
              <h3 className="font-heading text-lg text-stone-50 mb-2">Client Satisfaction</h3>
              <p className="font-body text-sm text-stone-50/60 leading-relaxed">We take pride in every detail, ensuring our clients are completely satisfied with the finished result.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Panels with Parallax */}
      <section 
        ref={section1Ref}
        className="section-padding bg-stone-50 relative" 
        data-testid="service-panels-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Section header */}
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-body text-xs uppercase tracking-widest text-bronze mb-4 block">Our Expertise</span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-charcoal">What We Do Best</h2>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 gap-8 md:gap-10"
            style={{ y: section1Y }}
          >
            {/* Luxury Bathrooms Panel */}
            <Link to="/services#bathrooms" className="group relative overflow-hidden rounded-sm min-h-[550px]" data-testid="bathroom-panel">
              <img
                src={MODERN_BATHROOM}
                alt="Luxury bathroom"
                className="w-full h-full object-cover absolute inset-0 transition-transform duration-1000 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500" />
              <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-10">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <span className="font-body text-xs uppercase tracking-widest text-bronze mb-3 block">Bathrooms</span>
                  <h3 className="font-heading text-3xl md:text-4xl text-stone-50 mb-4">
                    Luxury Bathrooms
                  </h3>
                  <p className="font-body text-base text-stone-50/80 mb-6 max-w-sm">
                    Complete bathroom design and installation finished to exceptional standards.
                  </p>
                  <span className="inline-flex items-center gap-2 font-body text-xs uppercase tracking-widest text-bronze group-hover:text-bronze-light transition-colors duration-300">
                    Discover More 
                    <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-2" />
                  </span>
                </motion.div>
              </div>
            </Link>

            {/* Residential Refurbishment Panel */}
            <Link to="/services#refurbishment" className="group relative overflow-hidden rounded-sm min-h-[550px]" data-testid="refurbishment-panel">
              <img
                src={MODERN_INTERIOR}
                alt="Home refurbishment"
                className="w-full h-full object-cover absolute inset-0 transition-transform duration-1000 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500" />
              <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-10">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <span className="font-body text-xs uppercase tracking-widest text-bronze mb-3 block">Refurbishment</span>
                  <h3 className="font-heading text-3xl md:text-4xl text-stone-50 mb-4">
                    Residential Refurbishment
                  </h3>
                  <p className="font-body text-base text-stone-50/80 mb-6 max-w-sm">
                    Carefully managed home upgrades and interior transformations.
                  </p>
                  <span className="inline-flex items-center gap-2 font-body text-xs uppercase tracking-widest text-bronze group-hover:text-bronze-light transition-colors duration-300">
                    Discover More 
                    <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-2" />
                  </span>
                </motion.div>
              </div>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Our Projects Slideshow */}
      <section className="py-20 bg-charcoal overflow-hidden" data-testid="projects-slideshow-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex items-end justify-between"
          >
            <div>
              <span className="font-body text-xs uppercase tracking-widest text-bronze mb-4 block">Our Projects</span>
              <h2 className="font-heading text-3xl md:text-4xl text-stone-50">Featured Work</h2>
            </div>
            <Link
              to="/portfolio"
              data-testid="slideshow-portfolio-link"
              className="hidden md:inline-flex items-center gap-2 font-body text-xs uppercase tracking-widest text-bronze hover:text-bronze-light transition-colors duration-300"
            >
              Check out our work
              <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>

        <ProjectSlideshow />

        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-8 md:hidden">
          <Link
            to="/portfolio"
            data-testid="slideshow-portfolio-link-mobile"
            className="inline-flex items-center gap-2 font-body text-xs uppercase tracking-widest text-bronze hover:text-bronze-light transition-colors duration-300"
          >
            Check out our work
            <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      {/* ===== TESTIMONIALS SECTION ===== */}
      <section className="bg-[#FAF8F5]" data-testid="reviews-section">

        {/* Testimonial Hero - Split Layout */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-24">
          <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
            {/* Left - Text */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="font-body text-xs uppercase tracking-widest text-bronze mb-4 block">Testimonials</span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-charcoal mb-5 leading-tight">
                What Our<br />Clients Say
              </h2>
              <div className="w-10 h-[2px] bg-bronze mb-6" />
              <p className="font-body text-sm text-charcoal/55 leading-relaxed max-w-sm">
                Real feedback from homeowners across Northamptonshire who trusted us with their bathrooms and properties.
              </p>
            </motion.div>

            {/* Right - Premium Detail Image */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="relative rounded-lg overflow-hidden aspect-[4/3] group"
            >
              <img
                src="https://images.pexels.com/photos/35209393/pexels-photo-35209393.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Warm bathroom vanity with candles and premium finishes"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                data-testid="testimonial-hero-image"
              />
            </motion.div>
          </div>
        </div>

        {/* Trust Bar - Floating Panel */}
        <div className="max-w-5xl mx-auto px-6 md:px-12 -mt-14 relative z-10 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-lg shadow-lg shadow-charcoal/5 border border-charcoal/5 px-6 py-5"
            data-testid="trust-bar"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-0 md:divide-x md:divide-charcoal/10">
              {/* Google Reviews */}
              <div className="flex items-center gap-3 md:justify-center md:px-4">
                <svg className="w-8 h-8 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <div>
                  <div className="flex items-center gap-1.5">
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <svg key={`gb-${i}`} className="w-4 h-4 text-[#F4B400]" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <span className="font-heading text-lg text-charcoal font-bold">5.0</span>
                  </div>
                  <p className="font-body text-[10px] text-charcoal/40 mt-0.5">Based on 99+ reviews</p>
                </div>
              </div>

              {/* Happy Customers */}
              <div className="flex items-center gap-3 md:justify-center md:px-4">
                <div className="w-9 h-9 rounded-full bg-bronze/10 flex items-center justify-center flex-shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-bronze">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
                  </svg>
                </div>
                <div>
                  <span className="font-heading text-lg text-charcoal font-bold">99+</span>
                  <p className="font-body text-[10px] text-charcoal/40 mt-0.5">Happy Customers</p>
                </div>
              </div>

              {/* Customer Focused */}
              <div className="flex items-center gap-3 md:justify-center md:px-4">
                <div className="w-9 h-9 rounded-full bg-bronze/10 flex items-center justify-center flex-shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-bronze">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <span className="font-body text-xs text-charcoal font-semibold">Customer Focused</span>
                  <p className="font-body text-[10px] text-charcoal/40 mt-0.5">Built around your vision</p>
                </div>
              </div>

              {/* More Reviews */}
              <div className="flex items-center gap-3 md:justify-center md:px-4">
                <div className="w-9 h-9 rounded-full bg-bronze/10 flex items-center justify-center flex-shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-bronze">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                  </svg>
                </div>
                <p className="font-body text-[10px] text-charcoal/50 leading-snug max-w-[140px]">
                  There are many more reviews from our amazing customers.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Three Testimonial Cards */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "David T.",
                location: "East Northampton",
                review: "Pawel at PJMW Services was professional, reliable, and paid incredible attention to every detail. The quality of workmanship is outstanding, and everything was completed to a very high standard.",
                image: "https://images.pexels.com/photos/19991834/pexels-photo-19991834.jpeg?auto=compress&cs=tinysrgb&w=600"
              },
              {
                name: "Magda W.",
                location: "Towcester",
                review: "Very professional and reliable service. Great attention to details and clear communication. I was very pleased with the final result. Highly recommended with very competitive price.",
                image: "https://images.unsplash.com/photo-1643949700215-e61cdca053f7?crop=entropy&cs=srgb&fm=jpg&w=600&q=80"
              },
              {
                name: "Val' Em",
                location: "Dallington, Northampton",
                review: "The best service I have used yet. Pawel\u2019s expertise, due diligence and advice was exceptional. The end product was even better than what I had envisaged. I cannot recommend his services highly enough.",
                image: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?crop=entropy&cs=srgb&fm=jpg&w=600&q=80"
              }
            ].map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.12 }}
                className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-500 group"
                data-testid={`review-${index}`}
              >
                {/* Card Image */}
                <div className="relative overflow-hidden aspect-[16/10]">
                  <img
                    src={testimonial.image}
                    alt={`Bathroom detail for ${testimonial.name} review`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                </div>

                {/* Card Content */}
                <div className="p-7">
                  {/* Stars + Quote */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <svg key={`s-${index}-${i}`} className="w-4 h-4 text-bronze" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <span className="font-heading text-4xl text-charcoal/8 leading-none select-none">&ldquo;</span>
                  </div>

                  {/* Review Text */}
                  <p className="font-body text-sm text-charcoal/60 leading-relaxed mb-6">
                    {testimonial.review}
                  </p>

                  {/* Divider + Author */}
                  <div className="border-t border-bronze/20 pt-4">
                    <p className="font-body font-semibold text-charcoal text-sm">{testimonial.name}</p>
                    <p className="font-body text-[11px] text-charcoal/40">{testimonial.location}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* See More Reviews */}
        <div className="text-center pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-10 h-10 rounded-full bg-bronze/10 flex items-center justify-center mx-auto mb-4">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-bronze">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
              </svg>
            </div>
            <h3 className="font-heading text-2xl md:text-3xl text-charcoal mb-2">See More Reviews</h3>
            <p className="font-body text-sm text-charcoal/50 mb-6 max-w-md mx-auto">
              We&apos;re proud of our reputation. See more feedback from customers we&apos;ve worked with.
            </p>
            <a
              href="https://www.google.com/maps/place/PJMW+Services+Luxury+Bathrooms+%26+Renovations/@52.1892433,-1.1157445,11z/data=!3m1!4b1!4m6!3m5!1s0xab9e2ad8255f34a3:0xd1c65bc004484c8f!8m2!3d52.1893586!4d-0.9505951!16s%2Fg%2F11nq2vwvp1?entry=ttu&g_ep=EgoyMDI2MDgxOS4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-button"
              className="inline-flex items-center gap-2 uppercase tracking-widest text-xs font-semibold px-8 py-4 bg-charcoal text-stone-50 rounded-sm hover:bg-charcoal/90 transition-all duration-300"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              View More Reviews on Google
              <ArrowRight size={14} strokeWidth={1.5} />
            </a>
          </motion.div>
        </div>

        {/* Consultation CTA Panel */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="bg-charcoal rounded-xl overflow-hidden"
            data-testid="testimonial-cta-panel"
          >
            <div className="grid md:grid-cols-[280px_1fr] lg:grid-cols-[340px_1fr]">
              {/* Left - Material/Plans Image */}
              <div className="relative aspect-[4/3] md:aspect-auto overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1752321531399-1e2b66043b52?crop=entropy&cs=srgb&fm=jpg&w=600&q=80"
                  alt="Material samples and renovation planning"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Right - CTA Content */}
              <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-center">
                <span className="font-body text-[10px] uppercase tracking-[0.2em] text-bronze font-semibold mb-3 block">
                  Let&apos;s Bring Your Vision to Life
                </span>
                <h3 className="font-heading text-2xl md:text-3xl text-stone-50 mb-4">
                  Ready to Create Your<br />Perfect Bathroom?
                </h3>
                <p className="font-body text-sm text-stone-50/50 leading-relaxed mb-8 max-w-md">
                  Get in touch today for a free, no-obligation consultation and let&apos;s start planning your dream bathroom.
                </p>
                <Link
                  to="/contact"
                  data-testid="testimonial-cta-contact"
                  className="group relative inline-flex items-center justify-center gap-2 uppercase tracking-widest text-xs font-semibold px-8 py-4 bg-bronze text-charcoal rounded-sm overflow-hidden transition-all duration-500 self-start"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    Get Your Free Consultation
                    <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                  <div className="absolute inset-0 bg-bronze-light transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Three Trust Points */}
        <div className="max-w-5xl mx-auto px-6 md:px-12 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-8"
          >
            {[
              {
                title: "Expert Craftsmanship",
                desc: "Quality workmanship with precision in every detail.",
                icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-bronze"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" /></svg>
              },
              {
                title: "Clear Communication",
                desc: "Open, honest and reliable throughout your project.",
                icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-bronze"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
              },
              {
                title: "Quality Built to Last",
                desc: "Premium materials and finishes designed to stand the test of time.",
                icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-bronze"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
              }
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-charcoal/5 flex items-center justify-center flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-body text-sm font-semibold text-charcoal mb-1">{item.title}</h4>
                  <p className="font-body text-[11px] text-charcoal/45 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="bg-[#F5F0EB]" data-testid="craftsmanship-section">
        {/* Hero Introduction */}
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-0 items-stretch">
            {/* Left - Text Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="py-16 md:py-24 pr-0 lg:pr-16 flex flex-col justify-center"
            >
              <span className="font-body text-xs uppercase tracking-[0.2em] text-bronze mb-4 block">What Makes Us Different</span>
              <div className="w-10 h-[2px] bg-bronze mb-8" />
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-charcoal leading-[1.15] mb-6">
                The Details Make<br />the Difference
              </h2>
              <p className="font-body text-sm text-cool-grey leading-relaxed mb-10 max-w-md">
                Exceptional results come from exceptional attention to detail. From perfect mitred cuts to flawless silicone work, we deliver a level of craftsmanship that sets our bathrooms and properties apart.
              </p>

              {/* Icon Badges */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {[
                  { label: "Attention to\nEvery Detail", icon: <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="22"/><line x1="2" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="22" y2="12"/></svg> },
                  { label: "Premium\nMaterials", icon: <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg> },
                  { label: "Built to Last,\nFinished to Perfection", icon: <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg> },
                  { label: "Quality Without\nCompromise", icon: <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg> }
                ].map((badge) => (
                  <div key={badge.label} className="text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 text-bronze mb-2">{badge.icon}</div>
                    <p className="font-body text-[10px] uppercase tracking-wider text-charcoal/70 whitespace-pre-line leading-tight">{badge.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right - Hero Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.2 }}
              className="relative overflow-hidden min-h-[400px] lg:min-h-0"
            >
              <img
                src={CRAFT_HERO_NICHE}
                alt="Recessed tiled shower niche with LED lighting and premium products"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>

        {/* Feature Rows */}
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {[
            {
              title: "Precision Mitred Cuts",
              description: "Seamless lines, precise angles and a flawless finish. Every edge is cut and finished to perfection.",
              images: [
                { src: CRAFT_MITRE_1, alt: "Close-up of a perfectly mitred external tile corner", tall: true },
                { src: CRAFT_MITRE_2, alt: "Precise mitre joint on tile corner edge" },
                { src: CRAFT_NICHE_DETAIL, alt: "Perfectly aligned grout lines in shower niche" }
              ]
            },
            {
              title: "Immaculate Finishing",
              description: "Clean, smooth and consistent silicone work for a professional, high-end finish. Neat lines that stand the test of time.",
              images: [
                { src: CRAFT_SILICONE, alt: "Perfectly smooth silicone joint around basin", wide: true },
                { src: CRAFT_SILICONE_GLASS, alt: "Clean silicone seal where glass meets tiles" }
              ]
            },
            {
              title: "Floor-Level Showers",
              description: "Sleek, practical and modern. Our level access showers create a smooth, open feel with perfect drainage and a premium finish.",
              images: [
                { src: CRAFT_FLOOR_SHOWER, alt: "Flush floor-level shower tray with linear drain", wide: true },
                { src: CRAFT_SHOWER_MIXER, alt: "Brushed brass shower mixer against warm tiles" }
              ]
            },
            {
              title: "Attention to Every Detail",
              description: "We don't cut corners. Every element is completed with care, precision and a commitment to excellence.",
              images: [
                { src: CRAFT_VANITY, alt: "Premium vanity with vessel basin and ambient lighting", wide: true },
                { src: CRAFT_JUNCTION, alt: "Perfect junction where tiles meet vanity cabinetry" }
              ]
            }
          ].map((row, rowIndex) => (
            <motion.div
              key={row.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.7 }}
              className="grid lg:grid-cols-[280px_1fr] gap-8 py-1 border-t border-stone-300/40"
              data-testid={`craft-row-${rowIndex}`}
            >
              {/* Text */}
              <div className="py-8 lg:py-12">
                <h3 className="font-body text-xs uppercase tracking-[0.2em] text-bronze mb-3">{row.title}</h3>
                <div className="w-6 h-[2px] bg-bronze/50 mb-4" />
                <p className="font-body text-sm text-cool-grey leading-relaxed">{row.description}</p>
              </div>

              {/* Images */}
              <div className={`grid gap-3 ${row.images.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                {row.images.map((img, imgIndex) => (
                  <div
                    key={img.alt}
                    className={`relative overflow-hidden group ${img.tall ? 'row-span-1' : ''}`}
                  >
                    <img
                      src={img.src}
                      alt={img.alt}
                      className="w-full h-full object-cover aspect-[4/3] transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Statement */}
        <div className="bg-[#EDE8E1] mt-1">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
            <div className="grid lg:grid-cols-[1fr_auto] gap-12 items-center">
              {/* Left - Statement */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h3 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-charcoal leading-tight mb-4">
                  Quality You Can See.<br />Craftsmanship You Can Trust.
                </h3>
                <p className="font-body text-sm text-cool-grey leading-relaxed max-w-lg mb-8">
                  We take pride in the details that turn a good renovation into an exceptional one — delivering refined finishes designed to stand the test of time.
                </p>
                <Link
                  to="/contact"
                  data-testid="craftsmanship-cta-btn"
                  className="group relative inline-flex items-center justify-center gap-2 uppercase tracking-widest text-xs font-semibold px-10 py-5 bg-bronze text-charcoal rounded-sm overflow-hidden transition-all duration-500"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    Get a Free Quote
                    <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                  <div className="absolute inset-0 bg-bronze-light transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </Link>
              </motion.div>

              {/* Right - Trust Icons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="grid grid-cols-2 sm:grid-cols-4 gap-8"
              >
                {[
                  { label: "Experienced\nProfessionals", icon: <svg className="w-9 h-9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
                  { label: "Reliable &\nTrustworthy", icon: <svg className="w-9 h-9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg> },
                  { label: "On Time,\nEvery Time", icon: <svg className="w-9 h-9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/><path d="M8 18h.01"/><path d="M12 18h.01"/></svg> },
                  { label: "High-End Finishes\nEvery Project", icon: <svg className="w-9 h-9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> }
                ].map((item) => (
                  <div key={item.label} className="text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 text-bronze mb-2">{item.icon}</div>
                    <p className="font-body text-[10px] uppercase tracking-wider text-charcoal/70 whitespace-pre-line leading-tight">{item.label}</p>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Get In Touch CTA */}
      <section className="relative min-h-[50vh] flex items-center overflow-hidden" data-testid="bathroom-cta-section">
        <div className="absolute inset-0 bg-charcoal" />
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_3e849b0a-3eb9-4a57-9473-38116027ffed/artifacts/ij4mf2ap_pawiale_Modern_UK_family_bathroom_realistic_residential_home__b32956b1-ca1a-4585-9726-d4aa5dbd284c_1.png"
            alt="Modern bathroom"
            className="w-full h-full object-cover opacity-25"
          />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center py-24 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-body text-xs uppercase tracking-widest text-bronze mb-4 block">Start Your Project</span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-stone-50 mb-4">
              Ready to Transform Your Space?
            </h2>
            <p className="font-body text-base text-stone-300 mb-10 max-w-xl mx-auto leading-relaxed">
              Get in touch today for a free, no-obligation consultation. We'd love to discuss your project and bring your vision to life.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/contact"
              data-testid="bathroom-cta-contact-btn"
              className="group relative inline-flex items-center justify-center gap-2 uppercase tracking-widest text-xs font-semibold px-10 py-5 bg-bronze text-charcoal rounded-sm overflow-hidden transition-all duration-500"
            >
              <span className="relative z-10 flex items-center gap-2">
                Get in Contact
                <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
              </span>
              <div className="absolute inset-0 bg-bronze-light transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
            </Link>
            <Link
              to="/portfolio"
              data-testid="bathroom-cta-portfolio-btn"
              className="inline-flex items-center justify-center gap-2 uppercase tracking-widest text-xs font-semibold px-10 py-5 border-2 border-stone-50 text-stone-50 rounded-sm hover:bg-stone-50 hover:text-charcoal transition-all duration-500"
            >
              View Our Portfolio
              <Play size={14} strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
